import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import PageHero from "@/components/PageHero";
import { Mail, Phone, MapPin, MessageCircle, Send } from "lucide-react";

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", form_type: "contact" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/submissions", form);
      toast.success("Mensagem enviada! Retornaremos em breve.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "", form_type: "contact" });
    } catch {
      toast.error("Não foi possível enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[color:var(--marupiara-ivory)]">
      <PageHero image={settings.contact_hero_image} overline="Contato" title="Vamos conversar sobre seu livro." />
      <section className="py-16">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="overline text-[color:var(--marupiara-gold-dark)] mb-2">Informações</div>
              <p className="text-neutral-700 leading-relaxed">Envie-nos sua mensagem ou entre em contato pelos canais abaixo. Também aceitamos orçamentos e projetos editoriais.</p>
            </div>
            {settings.email && <div className="flex gap-3 items-start"><Mail size={18} className="text-[color:var(--marupiara-gold-dark)] mt-1" /><div><div className="overline text-[10px] text-[color:var(--marupiara-text-on-light-muted)]">E-mail</div><a href={`mailto:${settings.email}`} className="text-[color:var(--marupiara-obsidian)] hover:text-[color:var(--marupiara-gold-dark)]">{settings.email}</a></div></div>}
            {settings.phone && <div className="flex gap-3 items-start"><Phone size={18} className="text-[color:var(--marupiara-gold-dark)] mt-1" /><div><div className="overline text-[10px] text-[color:var(--marupiara-text-on-light-muted)]">Telefone</div><span>{settings.phone}</span></div></div>}
            {settings.whatsapp && <div className="flex gap-3 items-start"><MessageCircle size={18} className="text-[color:var(--marupiara-gold-dark)] mt-1" /><div><div className="overline text-[10px] text-[color:var(--marupiara-text-on-light-muted)]">WhatsApp</div><a href={`https://wa.me/${settings.whatsapp}`} target="_blank" rel="noreferrer" className="hover:text-[color:var(--marupiara-gold-dark)]">Chamar no WhatsApp</a></div></div>}
            {settings.address && <div className="flex gap-3 items-start"><MapPin size={18} className="text-[color:var(--marupiara-gold-dark)] mt-1" /><div><div className="overline text-[10px] text-[color:var(--marupiara-text-on-light-muted)]">Endereço</div><span>{settings.address}</span></div></div>}
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-3 bg-white p-8 border border-[color:var(--marupiara-gold)]/20 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="overline text-[10px] text-[color:var(--marupiara-text-on-light-muted)]">Nome *</label>
                <input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 px-3 py-2.5 border border-neutral-300 focus:border-[color:var(--marupiara-gold-dark)] focus:outline-none" />
              </div>
              <div>
                <label className="overline text-[10px] text-[color:var(--marupiara-text-on-light-muted)]">E-mail *</label>
                <input required type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1 px-3 py-2.5 border border-neutral-300 focus:border-[color:var(--marupiara-gold-dark)] focus:outline-none" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="overline text-[10px] text-[color:var(--marupiara-text-on-light-muted)]">Telefone</label>
                <input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full mt-1 px-3 py-2.5 border border-neutral-300 focus:border-[color:var(--marupiara-gold-dark)] focus:outline-none" />
              </div>
              <div>
                <label className="overline text-[10px] text-[color:var(--marupiara-text-on-light-muted)]">Assunto</label>
                <select data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value, form_type: e.target.value === "Orçamento" ? "quote" : "contact" })} className="w-full mt-1 px-3 py-2.5 border border-neutral-300 focus:border-[color:var(--marupiara-gold-dark)] focus:outline-none bg-white">
                  <option value="">Selecione…</option>
                  <option>Orçamento</option>
                  <option>Publicar livro</option>
                  <option>Parceria</option>
                  <option>Outro</option>
                </select>
              </div>
            </div>
            <div>
              <label className="overline text-[10px] text-[color:var(--marupiara-text-on-light-muted)]">Mensagem *</label>
              <textarea required data-testid="contact-message" rows="5" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full mt-1 px-3 py-2.5 border border-neutral-300 focus:border-[color:var(--marupiara-gold-dark)] focus:outline-none resize-none" />
            </div>
            <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-gold w-full justify-center">
              <Send size={16} /> {loading ? "Enviando…" : "Enviar mensagem"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
